import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { FaCheck, FaTimes, FaEye, FaUserCheck, FaUserTimes } from 'react-icons/fa';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: '',
    verificationStatus: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });
  const [docUser, setDocUser] = useState(null);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.current]);

  const fetchUsers = async () => {
    try {
      const params = {
        page: pagination.current,
        limit: 10,
        ...filters
      };
      
      const response = await api.get('/api/admin/users', { params });
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Fetch users error:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId, status) => {
    try {
      await api.put(`/api/admin/users/${userId}/verify`, { status });
      toast.success(`User ${status} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Verify user error:', error);
      toast.error('Failed to update user verification');
    }
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      await api.put(`/api/admin/users/${userId}/status`, { isActive });
      toast.success(`User ${isActive ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Toggle user status error:', error);
      toast.error('Failed to update user status');
    }
  };

  const normalizeFileUrl = (p) => {
    if (!p) return '';
    let cleaned = p.replace(/\\/g, '/');
    cleaned = cleaned.replace(/^server\//, '');
    if (!cleaned.startsWith('uploads/')) {
      const idx = cleaned.indexOf('uploads/');
      if (idx !== -1) cleaned = cleaned.slice(idx);
    }
    return '/' + cleaned;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">User Management</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage users, verifications, and account status</p>
          </div>

          {/* Filters */}
          <div className="card mb-4 sm:mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  placeholder="Search by name, email, or phone"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="input-field"
                >
                  <option value="">All Roles</option>
                  <option value="rider">Rider</option>
                  <option value="passenger">Passenger</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Verification</label>
                <select
                  value={filters.verificationStatus}
                  onChange={(e) => handleFilterChange('verificationStatus', e.target.value)}
                  className="input-field"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setFilters({ role: '', verificationStatus: '', search: '' })}
                  className="btn-secondary w-full"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="card">
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verification
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      Joined
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td className="px-3 sm:px-6 py-4">
                        <div className="flex items-center min-w-[200px]">
                          <img
                            src={normalizeFileUrl(user.photo) || '/default-avatar.png'}
                            alt={user.name}
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full mr-2 sm:mr-4 flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{user.name}</div>
                            <div className="text-xs sm:text-sm text-gray-500 truncate">{user.email}</div>
                            <div className="text-xs sm:text-sm text-gray-500">{user.phone}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <span className={`badge text-xs ${
                          user.role === 'rider' ? 'badge-info' :
                          user.role === 'passenger' ? 'badge-success' : 'badge-warning'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <span className={`badge text-xs ${
                          user.verificationStatus === 'verified' ? 'badge-success' :
                          user.verificationStatus === 'pending' ? 'badge-warning' : 'badge-danger'
                        }`}>
                          {user.verificationStatus}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <span className={`badge text-xs ${user.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm font-medium">
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          {user.role === 'rider' && (
                            <>
                              {user.documents && (user.documents.licensePhoto || user.documents.vehiclePhoto || user.documents.idProof) && (
                                <button
                                  onClick={() => setDocUser(user)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="View Documents"
                                >
                                  <FaEye />
                                </button>
                              )}
                              {user.verificationStatus === 'pending' && (
                                <>
                              <button
                                onClick={() => handleVerifyUser(user._id, 'verified')}
                                className="text-green-600 hover:text-green-900"
                                title="Verify"
                              >
                                <FaCheck />
                              </button>
                              <button
                                onClick={() => handleVerifyUser(user._id, 'rejected')}
                                className="text-red-600 hover:text-red-900"
                                title="Reject"
                              >
                                <FaTimes />
                              </button>
                                </>
                              )}
                            </>
                          )}
                          <button
                            onClick={() => handleToggleUserStatus(user._id, !user.isActive)}
                            className={`${user.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                            title={user.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {user.isActive ? <FaUserTimes /> : <FaUserCheck />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.current - 1) * 10) + 1} to {Math.min(pagination.current * 10, pagination.total)} of {pagination.total} results
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                      disabled={pagination.current === 1}
                      className="btn-secondary disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm text-gray-700">
                      Page {pagination.current} of {pagination.pages}
                    </span>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                      disabled={pagination.current === pagination.pages}
                      className="btn-secondary disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    {docUser && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Documents - {docUser.name}</h3>
            <button onClick={() => setDocUser(null)} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>
          {docUser.role === 'rider' && (
            <div className="card mb-4">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">Vehicle Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
                <div>
                  <div className="text-gray-500">Type</div>
                  <div className="font-medium">{docUser.vehicleInfo?.type || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Model</div>
                  <div className="font-medium">{docUser.vehicleInfo?.model || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Year</div>
                  <div className="font-medium">{docUser.vehicleInfo?.year || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Color</div>
                  <div className="font-medium">{docUser.vehicleInfo?.color || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Plate Number</div>
                  <div className="font-medium">{docUser.vehicleInfo?.plateNumber || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">License No.</div>
                  <div className="font-medium">{docUser.licenseNumber || '-'}</div>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">License</div>
              {docUser.documents?.licensePhoto ? (
                <a href={normalizeFileUrl(docUser.documents.licensePhoto)} target="_blank" rel="noreferrer" className="block">
                  <img src={normalizeFileUrl(docUser.documents.licensePhoto)} alt="License" className="w-full h-40 object-cover rounded border" />
                </a>
              ) : (
                <div className="text-gray-500 text-sm">Not uploaded</div>
              )}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Vehicle Photo</div>
              {docUser.documents?.vehiclePhoto ? (
                <a href={normalizeFileUrl(docUser.documents.vehiclePhoto)} target="_blank" rel="noreferrer" className="block">
                  <img src={normalizeFileUrl(docUser.documents.vehiclePhoto)} alt="Vehicle" className="w-full h-40 object-cover rounded border" />
                </a>
              ) : (
                <div className="text-gray-500 text-sm">Not uploaded</div>
              )}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">ID Proof</div>
              {docUser.documents?.idProof ? (
                <a href={normalizeFileUrl(docUser.documents.idProof)} target="_blank" rel="noreferrer" className="block">
                  <img src={normalizeFileUrl(docUser.documents.idProof)} alt="ID Proof" className="w-full h-40 object-cover rounded border" />
                </a>
              ) : (
                <div className="text-gray-500 text-sm">Not uploaded</div>
              )}
            </div>
          </div>
          {docUser.role === 'rider' && docUser.verificationStatus === 'pending' && (
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => { setDocUser(null); handleVerifyUser(docUser._id, 'rejected'); }} className="btn-secondary">Reject</button>
              <button onClick={() => { setDocUser(null); handleVerifyUser(docUser._id, 'verified'); }} className="btn-primary">Verify</button>
            </div>
          )}
        </div>
      </div>
    )}
  </>
  );
};

export default AdminUsers;
